// Global SweetAlert -> toast patch.
//
// The app fires 120+ `Swal.fire(...)` notifications as big, centered, blocking
// modals with a large image and an "Ok" button. This patches the shared Swal
// singleton ONCE so every notification-style call instead renders as a clean
// top toast that slides in and auto-dismisses — without editing each call site.
//
// Interactive dialogs (confirm/cancel, inputs, deny) are left as real modals so
// their returned promise (isConfirmed / value) keeps working.
import Swal from 'sweetalert2';

const nativeFire = Swal.fire.bind(Swal);

// Support both Swal.fire(options) and Swal.fire(title, text, icon).
const normalize = (args) => {
  if (typeof args[0] === 'string') {
    return { title: args[0], text: args[1], icon: args[2] };
  }
  return { ...(args[0] || {}) };
};

const guessIcon = (opts) => {
  if (opts.icon && opts.icon !== 'success') return opts.icon;
  // `type` is the deprecated v9 prop still used across this codebase.
  if (opts.type && ['success', 'error', 'warning', 'info'].includes(opts.type)) {
    // fall through to title heuristic only if type is the default 'success'
    if (opts.type !== 'success') return opts.type;
  }
  const title = `${opts.title || ''} ${opts.text || ''}`.toLowerCase();
  if (
    /error|wrong|fail|failed|invalid|required|utilized|quota|not |denied|expired|unable|problem/.test(
      title
    )
  ) {
    return 'error';
  }
  if (/warn|already|please|cannot|can't|must/.test(title)) return 'warning';
  return opts.icon || 'success';
};

Swal.fire = function patchedFire(...args) {
  const opts = normalize(args);

  // Keep real modals for anything the caller needs to interact with.
  const isInteractive =
    opts.showCancelButton ||
    opts.showDenyButton ||
    opts.input ||
    typeof opts.preConfirm === 'function';

  // Keep real BLOCKING modals too — loaders/progress dialogs. These set
  // allowOutsideClick:false / allowEscapeKey:false and usually a custom didOpen
  // (e.g. Swal.showLoading()). Downgrading them to a toast dropped the spinner,
  // auto-dismissed after the timer, and — because guessIcon() matched words
  // like "not"/"please" in "Please wait, do not close this window" — slapped a
  // red error ✗ on what is actually a loading state. Let them render natively.
  const isBlocking =
    opts.toast === false ||
    opts.allowOutsideClick === false ||
    opts.allowEscapeKey === false ||
    typeof opts.didOpen === 'function';

  if (isInteractive || isBlocking) {
    return nativeFire(opts);
  }

  return nativeFire({
    toast: true,
    position: 'top',
    icon: guessIcon(opts),
    title: opts.title,
    text: opts.text,
    showConfirmButton: false,
    timer: 2600,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    },
  });
};

export default Swal;
